import React, { useState, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Spinner } from 'reactstrap';
import { Activity } from '../../../Model/Activity/Activity';
import { ActivityLog, isActivityLogValid } from '../../../Model/Activity/ActivityLog';
import { useToast } from '../../../Hooks/useToast';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { useRootStore } from '../../../Store/useRootStore';
import { useLanguage } from '../../../Hooks/useLanguage';
import { useSaveActivityLog } from '../../../Hooks/useActivityLogMutations';
import { useActivitySearch } from './useActivitySearch';
import { useActivityLogForm } from './useActivityLogForm';
import { ActivitySelectStep } from './ActivitySelectStep';
import { ActivityEditStep } from './ActivityEditStep';
import {
  StyledModal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  TitleIcon,
  CloseButton,
  ModalBody,
  ModalFooter,
  SecondaryButton,
  PrimaryButton,
} from './LogActivityModal.styles';

export type LogActivityModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onActivityLogged?: (activityLog: ActivityLog) => void;
};

type Step = 'select' | 'edit';

export const LogActivityModal: React.FC<LogActivityModalProps> = observer(({ isOpen, onClose, onActivityLogged }) => {
  const { t } = useTrans();
  const { authentication } = useRootStore();
  const { language } = useLanguage();
  const toast = useToast();
  const bmr = 1800; // TODO: Get from athlete body values
  const saveActivityLog = useSaveActivityLog();

  const [step, setStep] = useState<Step>('select');
  const [saving, setSaving] = useState(false);

  const search = useActivitySearch({
    language: language,
    enabled: isOpen,
  });

  const form = useActivityLogForm();

  const handleClose = useCallback(() => {
    setStep('select');
    search.reset();
    form.reset();
    onClose();
  }, [onClose, search, form]);

  const handleSelectActivity = useCallback(
    (activity: Activity) => {
      form.createLogForActivity(activity);
      setStep('edit');
    },
    [form],
  );

  const handleBack = useCallback(() => {
    setStep('select');
    form.reset();
  }, [form]);

  const handleSave = useCallback(async () => {
    if (!form.activityLog) return;

    setSaving(true);
    try {
      const logToSave = { ...form.activityLog, athleteId: authentication.athleteId };
      const saved = await saveActivityLog.mutateAsync(logToSave);
      toast.showToast(
        t('activity.logged.success', { defaultValue: 'Activity logged successfully!' }),
        'success',
      );
      onActivityLogged?.(saved);
      handleClose();
    } catch (err: any) {
      console.error('Error saving activity log:', err?.response?.data ?? err?.message ?? err);
      toast.showToast(
        t('activity.logged.error', { defaultValue: 'Failed to log activity. Please try again.' }),
        'error',
      );
    } finally {
      setSaving(false);
    }
  }, [form.activityLog, authentication.athleteId, onActivityLogged, handleClose, toast, t, saveActivityLog]);

  return (
    <StyledModal isOpen={isOpen} toggle={handleClose} centered size="md">
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {step === 'select'
              ? t('activity.log.title', { defaultValue: 'Log Activity' })
              : t('activity.log.editTitle', { defaultValue: 'Activity Details' })}
          </ModalTitle>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {step === 'select' ? (
            <ActivitySelectStep
              activities={search.activities}
              loading={search.loading}
              loadingMore={search.loadingMore}
              hasMore={search.hasMore}
              query={search.query}
              language={language}
              onQueryChange={search.setQuery}
              onSelectActivity={handleSelectActivity}
              onLoadMore={search.loadMore}
            />
          ) : (
            form.activityLog && form.selectedActivity && (
              <ActivityEditStep
                activityLog={form.activityLog}
                activity={form.selectedActivity}
                language={language}
                date={form.date}
                startTime={form.startTime}
                endTime={form.endTime}
                trackingKeysToShow={form.trackingKeysToShow}
                bmr={bmr}
                onDataPointChange={form.setTrackingKeyValue}
                onDateChange={form.setDate}
                onStartTimeChange={form.setStartTime}
                onEndTimeChange={form.setEndTime}
                onBack={handleBack}
              />
            )
          )}
        </ModalBody>

        {step === 'edit' && (
          <ModalFooter>
            <SecondaryButton onClick={handleBack} disabled={saving}>
              {t('globals.back', { defaultValue: 'Back' })}
            </SecondaryButton>
            <PrimaryButton onClick={handleSave} disabled={saving || !(form.activityLog && isActivityLogValid(form.activityLog))}>
              {saving ? (
                <Spinner size="sm" color="light" />
              ) : (
                <>
                  <i className="bi bi-check2" />
                  {t('activity.log.save', { defaultValue: 'Log Activity' })}
                </>
              )}
            </PrimaryButton>
          </ModalFooter>
        )}
      </ModalContent>
    </StyledModal>
  );
});
